import * as React from 'react'
import { confirmCheckout, getStatus } from '../api'
import { readMobileToken } from '../config'

// Only the native app's own URL schemes are honored as a return target, so a
// stray/forged value can't turn into an open redirect.
function readAppReturnUrl(): string | null {
  const v = new URLSearchParams(window.location.search).get('return_url')
  if (v && /^(easytach|exp|exps):\/\//i.test(v)) return v
  return null
}

const card: React.CSSProperties = { maxWidth: 460, margin: '96px auto', padding: 24, textAlign: 'center' }

// Landing page after returning from Przelewy24. Finalizes the payment by
// sessionId (the async urlStatus webhook can't reach a dev box, and is racy in
// prod) and polls until the purchase is marked paid. Idempotent server-side.
export const MobileSuccessPage: React.FC = () => {
  const [paid, setPaid] = React.useState(false)
  const [failed, setFailed] = React.useState(false)
  const [tries, setTries] = React.useState(0)

  const sessionId = React.useMemo(
    () => new URLSearchParams(window.location.search).get('session_id'),
    [],
  )
  const token = React.useMemo(() => readMobileToken(), [])
  // Deep link back to the native app — present in THIS page's URL only when the
  // payment was started from the mobile app (the backend put it in the P24
  // urlReturn). Absent for web-app payments → just show the confirmation.
  const returnUrl = React.useMemo(() => readAppReturnUrl(), [])
  const [confirmedOnce, setConfirmedOnce] = React.useState(false)

  // Once paid, if we have a deep link, send the (in-app) browser to it so it
  // auto-closes and the native app resumes on the payment gate.
  React.useEffect(() => {
    if (paid && returnUrl) {
      const t = setTimeout(() => {
        window.location.href = returnUrl
      }, 1200)
      return () => clearTimeout(t)
    }
  }, [paid, returnUrl])

  React.useEffect(() => {
    if (paid || failed) return
    if (!token) {
      setFailed(true)
      return
    }
    if (tries >= 8) return
    const timer = setTimeout(async () => {
      try {
        if (sessionId && !confirmedOnce) {
          const res = await confirmCheckout(token, sessionId)
          if (res.failed) {
            setFailed(true)
            return
          }
          if (res.activated) setConfirmedOnce(true)
        }
        const status = await getStatus(token)
        if (status.paid) {
          setPaid(true)
          return
        }
      } catch {
        /* transient — retry on next tick */
      }
      setTries((n) => n + 1)
    }, confirmedOnce ? 800 : 2000)
    return () => clearTimeout(timer)
  }, [paid, failed, token, tries, sessionId, confirmedOnce])

  if (failed) {
    return (
      <div style={card}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
        <h1>Nie udało się potwierdzić płatności</h1>
        <p style={{ color: '#6b7280', marginTop: 8 }}>
          Jeśli pobrano środki, dostęp pojawi się za chwilę. Wróć do aplikacji i sprawdź ponownie.
        </p>
      </div>
    )
  }

  return (
    <div style={card}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{paid ? '✓' : '⏳'}</div>
      <h1>{paid ? 'Płatność potwierdzona' : 'Potwierdzanie płatności…'}</h1>
      <p style={{ color: '#6b7280', marginTop: 8 }}>
        {paid
          ? 'Dziękujemy! Twoje konto aplikacji mobilnej jest aktywne — wróć do aplikacji EasyTacho.'
          : 'Trwa potwierdzanie Twojej płatności w Przelewy24. To może chwilę potrwać.'}
      </p>
      {paid && returnUrl && (
        <a
          href={returnUrl}
          style={{
            display: 'inline-block', marginTop: 24, padding: '12px 24px',
            background: '#2563eb', color: '#fff', textDecoration: 'none',
            borderRadius: 6, fontWeight: 600,
          }}
        >
          Wróć do aplikacji
        </a>
      )}
    </div>
  )
}
